import { useEffect, useState } from 'react';

import { api, type ArAssetResponse } from '../../lib/api';

interface ArState {
  asset: ArAssetResponse | null;
  isLoading: boolean;
  error: string | null;
}

const USE_FIXTURES: boolean = String(import.meta.env.VITE_USE_FIXTURES ?? 'false') === 'true';

/**
 * Resolves the USDZ URL for a menu item via the API.
 *
 * In fixtures mode there are no real USDZ models — we return a
 * pending state so the UI shows the "AR coming soon" placeholder,
 * which matches the actual production state for items without
 * curated models.
 */
export function useArAsset(menuItemId: string): ArState & { request: () => Promise<void> } {
  const [state, setState] = useState<ArState>({
    asset: USE_FIXTURES ? { menuItemId, status: 'pending', usdzUrl: null, fromCache: false, assetId: '' } : null,
    isLoading: !USE_FIXTURES && Boolean(menuItemId),
    error: null,
  });

  useEffect(() => {
    if (USE_FIXTURES || !menuItemId) return;
    let cancelled = false;
    api
      .getArAsset(menuItemId)
      .then((asset) => { if (!cancelled) setState({ asset, isLoading: false, error: null }); })
      .catch((e: Error) => { if (!cancelled) setState({ asset: null, isLoading: false, error: e.message }); });
    return () => { cancelled = true; };
  }, [menuItemId]);

  const request = async (): Promise<void> => {
    if (USE_FIXTURES) {
      setState({
        asset: { menuItemId, status: 'generating', usdzUrl: null, fromCache: false, assetId: 'demo' },
        isLoading: false,
        error: null,
      });
      return;
    }
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const asset = await api.requestArGeneration(menuItemId);
      setState({ asset, isLoading: false, error: null });
    } catch (e) {
      setState({ asset: null, isLoading: false, error: e instanceof Error ? e.message : 'AR request failed' });
    }
  };

  return { ...state, request };
}
